import { ReportPath } from "@/models/route"
import type { ITableData, ITableRow } from "@/models/table"
import type { FeatureFlagName } from "@/services/feature"
import { ENERGY_AND_WATER_USE_LEGACY_WORKBOOK_ID } from "@/services/sigma"

import GresbLogo from "../assets/images/gresb-logo.png"
import GriLogo from "../assets/images/gri-logo.png"

export enum ReportGroup {
  Consultant,
  Custom,
  One,
  Two,
}

export interface ReportCard {
  buttonLink: string
  buttonText: string
  dataE2e?: string
  description?: string
  featureFlag?: FeatureFlagName
  group?: ReportGroup
  id?: string
  isPlaceholder?: boolean
  isVisibleToLimitedAccessUser?: boolean
  logo?: string
  newFeatureFlag?: FeatureFlagName
  title: string
}

type ReportCardName =
  | "ElectricityAnalytics"
  | "EnergyAndWaterUse"
  | "GhgEmissionInventory"
  | "Gresb"
  | "Gri"
  | "OutlierMonitor"
  | "QA"
  | "ResourceSummary"
  | "SiteInsights"

export const reportCard: Record<ReportCardName, ReportCard> = {
  ElectricityAnalytics: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "electricityAnalytics.description",
    featureFlag: "report_electricity_analytics",
    id: "72ed194e-9fd0-4631-a58d-f9068f8cf098",
    isVisibleToLimitedAccessUser: true,
    title: "electricityAnalytics.title",
  },
  EnergyAndWaterUse: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "energyAndWaterUse.description",
    featureFlag: "report_energy_and_water_use",
    group: ReportGroup.Consultant,
    id: ENERGY_AND_WATER_USE_LEGACY_WORKBOOK_ID,
    isVisibleToLimitedAccessUser: false,
    title: "energyAndWaterUse.title",
  },
  GhgEmissionInventory: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "ghgEmissionInventory.description",
    featureFlag: "report_ghg_emission_inventory",
    group: ReportGroup.Consultant,
    id: "7619d9b8-4129-42a3-af35-45f1485b61c0",
    isVisibleToLimitedAccessUser: false,
    title: "ghgEmissionInventory.title",
  },
  Gresb: {
    buttonLink: ReportPath.Gresb,
    buttonText: "viewButton",
    dataE2e: "gresb-data-export-button",
    featureFlag: "report_gresb",
    group: ReportGroup.Two,
    isVisibleToLimitedAccessUser: false,
    logo: GresbLogo,
    title: "gresb.title",
  },
  Gri: {
    buttonLink: ReportPath.Gri,
    buttonText: "viewButton",
    dataE2e: "gri-data-export-button",
    featureFlag: "report_gri",
    group: ReportGroup.Two,
    isVisibleToLimitedAccessUser: false,
    logo: GriLogo,
    title: "gri.title",
  },
  OutlierMonitor: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "outlierMonitor.description",
    id: "957deaac-51d1-4668-8131-91fc12339d7b",
    isVisibleToLimitedAccessUser: false,
    title: "outlierMonitor.title",
  },
  QA: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "qa.description",
    id: "cc321f0f-aae2-4164-8562-2febc6f29538",
    isVisibleToLimitedAccessUser: false,
    title: "qa.title",
  },
  ResourceSummary: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "resourceSummary.description",
    featureFlag: "report_resource_summary",
    id: "711aca88-5d83-4260-92ff-dcca2470d3df",
    isVisibleToLimitedAccessUser: true,
    title: "resourceSummary.title",
  },
  SiteInsights: {
    buttonLink: "",
    buttonText: "viewButton",
    description: "siteInsights.description",
    featureFlag: "report_site_insights",
    group: ReportGroup.Consultant,
    id: "a5842d93-e200-42f6-b989-8ba9d6982b07",
    isVisibleToLimitedAccessUser: false,
    title: "siteInsights.title",
  },
}

export const reportCards: ReportCard[] = Object.values(reportCard)

export interface Report<TRow extends ITableRow> {
  data: ITableData<TRow>
  name: string
}

export enum DataAlertInProgressText {
  AltText = "Report in progress stepper, preview available",
  Body = "Our Environmental Science & Sustainability team is reviewing your data. Feel free to download a preview but don’t use it for reporting just yet. We will let you know when your data is ready.",
  Title = "We’re preparing your data",
}

export enum DataAlertCompleteText {
  AltText = "Report complete stepper, ready to report",
  Body = "Our Environmental Science & Sustainability team has reviewed your data. This download can be used in your yearly reports.",
  Title = "Your data is ready for sharing",
}
